/**
 * Spend ledger derivation. The stored `spendings` are MANUAL only; the fuel/charge
 * and tyre "mirrors" are rebuilt here from fuelLogs/tyres at read time (never
 * stored, never re-imported) — exactly how the phones rebuild them on import.
 */
import type { CanonicalStore, Epoch } from "../model.ts";

export type LedgerSource = "manual" | "fuel" | "tyre";

export interface LedgerEntry {
  id: string;
  carId: string;
  amount: number;
  category: string;
  date: Epoch;
  currency: string;
  source: LedgerSource;
  /** Optional display name (e.g. a tyre set's name) shown next to the category. */
  label?: string;
}

export interface CategoryTotal {
  category: string;
  total: number;
  count: number;
}

export interface Ledger {
  entries: LedgerEntry[]; // newest first
  total: number;
  byCategory: CategoryTotal[]; // largest first
}

export function deriveLedger(store: CanonicalStore, carId?: string): Ledger {
  const currency = store.profile.displayCurrency;
  const entries: LedgerEntry[] = [];

  for (const s of store.spendings) {
    if (carId && s.carId !== carId) continue;
    entries.push({
      id: s.id, carId: s.carId, amount: s.amount, category: s.category ?? "Other",
      date: s.date ?? null, currency: s.currency, source: "manual",
    });
  }

  // Fuel/charge mirror: one per fuel log (PHEV combined counts as Fuel).
  for (const l of store.fuelLogs) {
    if (carId && l.carId !== carId) continue;
    entries.push({
      id: l.id, carId: l.carId, amount: l.totalCost,
      category: l.volumeAdded > 0 ? "Fuel" : "Charging",
      date: l.date ?? null, currency, source: "fuel",
    });
  }

  // Tyre purchase mirror ("Tires"), only when a price is known — from both
  // individual tyres and tyre sets.
  for (const t of store.tyres) {
    if (carId && t.carId !== carId) continue;
    if ((t.purchasePrice ?? 0) > 0) {
      entries.push({
        id: `tyre-${t.id}`, carId: t.carId, amount: t.purchasePrice as number,
        category: "Tires", date: t.purchaseDate ?? t.createdAt ?? null, currency, source: "tyre",
      });
    }
  }
  // A tyre set always shows as a named "Tires" line, but only adds its price to
  // the total when the user opted in (the constituent tyres are usually logged
  // separately, so counting the set too would double up).
  for (const ts of store.tyreSets) {
    if (carId && ts.carId !== carId) continue;
    const price = ts.purchasePrice ?? 0;
    if (price <= 0) continue;
    entries.push({
      id: `tyreset-${ts.id}`, carId: ts.carId,
      amount: ts.countPriceInSpend ? price : 0,
      category: "Tires", date: ts.purchaseDate ?? ts.createdAt ?? null, currency, source: "tyre",
      label: ts.name || ts.brand || "Tire set",
    });
  }

  entries.sort((a, b) => (b.date ?? 0) - (a.date ?? 0));

  const total = entries.reduce((s, e) => s + e.amount, 0);
  const map = new Map<string, CategoryTotal>();
  for (const e of entries) {
    const c = map.get(e.category) ?? { category: e.category, total: 0, count: 0 };
    c.total += e.amount;
    c.count += 1;
    map.set(e.category, c);
  }
  const byCategory = [...map.values()].sort((a, b) => b.total - a.total);

  return { entries, total, byCategory };
}
